import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { trackInitiateCheckout, trackViewContent } from "@/utils/metaPixel";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowRight, Bot, Globe, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import type { User } from "@supabase/supabase-js";
import { CountrySelector } from "@/components/CountrySelector";

// Add available tags
const TAGS = [
  "E-Commerce",
  "B2B",
  "B2C",
  "SaaS",
  "Retail",
  "Food & Drink",
  "Entertainment",
  "Healthcare",
  "Finance",
  "Education",
  "Other"
];

interface OnboardingData {
  organizationName: string;
  country: string;
  tags: string[];
  websiteUrl: string;
  websiteData?: {
    title: string;
    description: string;
    favicon: string;
    image?: string;
    domain?: string;
  };
}

const OnboardingFlow = () => {
  const [user, setUser] = useState<User | null>(null);
  const [step, setStep] = useState<number>(1); // Explicitly type as number
  const [showCountryField, setShowCountryField] = useState(true); // Always show country field
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false); // Separate state for checkout redirect
  const [data, setData] = useState<OnboardingData>({
    organizationName: "",
    country: "",
    tags: [],
    websiteUrl: "https://"
  });
  const navigate = useNavigate();
  const [cardContentHeight, setCardContentHeight] = useState<number | undefined>(undefined);
  const cardContentRef = useRef<HTMLDivElement>(null);
  const [typing, setTyping] = useState(false); // Move this up before useEffect
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const [redirectDots, setRedirectDots] = useState(0); // For animated dots

  // If user already has a website, redirect to dashboard
  useEffect(() => {
    const checkWebsite = async () => {
      if (!user) return;
      try {
        const { data: website, error } = await supabase
          .from('websites')
          .select('id')
          .eq('user_id', user.id)
          .eq('is_primary', true)
          .single();
        
        if (error && error.code !== 'PGRST116') {
          // PGRST116 is "not found" which is expected for new users
          console.error('Website check error:', error);
          return;
        }
        
        if (website && website.id) {
          navigate('/dashboard', { replace: true });
        }
      } catch (err) {
        console.error('Website check failed:', err);
      }
    };
    checkWebsite();
  }, [user, navigate]);

  // Measure content height on step/content change
  useEffect(() => {
    if (cardContentRef.current) {
      setCardContentHeight(cardContentRef.current.offsetHeight);
    }
  }, [step, data, loading, typing, showCountryField]);

  // Country field is always visible - no need for conditional logic

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        navigate('/');
        return;
      }
      setUser(session.user);
      
      // Track ViewContent for onboarding
      trackViewContent({
        content_name: 'Onboarding Flow',
        content_category: 'Account Setup',
      });
    };
    checkAuth();
  }, [navigate]);

  const DEBOUNCE_MS = 400;
  const API_TIMEOUT_MS = 8000; // 8 second timeout

  const isLikelyDomain = (url: string) => {
    // Simple check: must contain a dot and not be just protocol
    try {
      const u = new URL(url);
      return u.hostname.includes('.') && u.hostname.length > 3;
    } catch {
      return false;
    }
  };

  // Ensures the URL has a protocol, defaulting to https://
  const normalizeUrl = (url: string) => {
    if (!url) return '';
    if (/^https?:\/\//i.test(url)) return url;
    return `https://${url}`;
  };

  // Helper function to create a timeout promise
  const withTimeout = <T,>(promise: Promise<T>, timeoutMs: number): Promise<T> => {
    return Promise.race([
      promise,
      new Promise<T>((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), timeoutMs)
      )
    ]);
  };

  // Reset loading states when leaving step 3
  useEffect(() => {
    if (step !== 3) {
      // Clear any pending debounce
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      // Reset loading states
      setLoading(false);
      setTyping(false);
    }
  }, [step]);

  // Debounced website data fetch
  useEffect(() => {
    if (step !== 3) return;
    if (!isLikelyDomain(normalizeUrl(data.websiteUrl))) {
      setData(prev => ({ ...prev, websiteData: undefined }));
      setTyping(false);
      setLoading(false);
      return;
    }
    setTyping(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      setTyping(false);
      try {
        const apiCall = supabase.functions.invoke('parse-opengraph', {
          body: { url: normalizeUrl(data.websiteUrl) }
        });

        const { data: ogData, error } = await withTimeout(apiCall, API_TIMEOUT_MS);

        if (error) throw error;

        if (ogData.success) {
          setData(prev => ({
            ...prev,
            websiteData: {
              title: ogData.data.title,
              description: ogData.data.description,
              favicon: ogData.data.favicon,
              image: ogData.data.image,
              domain: ogData.data.domain
            }
          }));
        } else {
          throw new Error(ogData.error || 'Failed to parse website');
        }
      } catch (error) {
        console.error('Error fetching website data:', error);
        // Always set fallback data so user can continue
        try {
          const domain = new URL(normalizeUrl(data.websiteUrl)).hostname;
          setData(prev => ({
            ...prev,
            websiteData: {
              title: domain,
              description: `Official website for ${prev.organizationName}`,
              favicon: `https://www.google.com/s2/favicons?domain=${domain}&sz=64`,
              domain: domain
            }
          }));
        } catch {
          setData(prev => ({
            ...prev,
            websiteData: {
              title: 'Website',
              description: `Official website for ${prev.organizationName}`,
              favicon: '/placeholder.svg',
              domain: data.websiteUrl
            }
          }));
        }
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);
    
    // Cleanup function to clear timeout if component unmounts or dependencies change
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
    // eslint-disable-next-line
  }, [data.websiteUrl, step]);


  const handleNext = async () => {
    if (step === 1) {
      if (!data.organizationName.trim()) {
        toast.error("Please enter your organization name");
        return;
      }
      if (data.organizationName.trim().length < 3) {
        toast.error("Organization name must be at least 3 characters long");
        return;
      }
      if (!data.country.trim()) {
        toast.error("Please select your country");
        return;
      }
      setStep(2);
    } else if (step === 2) {
      if (data.tags.length === 0) {
        toast.error("Please select at least one tag");
        return;
      }
      setStep(3);
    } else if (step === 3) {
      if (!data.websiteUrl.trim() || data.websiteUrl === "https://") {
        toast.error("Please enter a valid website URL");
        return;
      }
      // Don't wait for website data fetch - proceed with whatever we have
      // Await website creation, then redirect to checkout
      await completeOnboarding();
    }
  };

  const handleBack = () => {
    // Reset loading states when going back
    setLoading(false);
    setTyping(false);
    // Clear any pending debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    setStep(step - 1);
  };

  const completeOnboarding = async () => {
    if (!user) return;
    console.log('Starting onboarding completion for user:', user.id);
    console.log('Onboarding data:', data);
    try {
      // Update or create profile - use first tag as industry if available
      const industry = data.tags.length > 0 ? data.tags[0] : 'Other';
      
      // First try to update existing profile
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          organization_name: data.organizationName,
          country: data.country,
          industry: industry
        })
        .eq('user_id', user.id);

      // If update fails (profile doesn't exist), create it
      if (profileError && profileError.code === 'PGRST116') {
        const { error: createError } = await supabase
          .from('profiles')
          .insert({
            user_id: user.id,
            organization_name: data.organizationName,
            country: data.country,
            industry: industry
          });

        if (createError) {
          console.error('Profile creation error:', createError);
          throw createError;
        }
      } else if (profileError) {
        console.error('Profile update error:', profileError);
        throw profileError;
      }

      // Extract domain safely
      let domain: string;
      try {
        domain = data.websiteData?.domain || new URL(normalizeUrl(data.websiteUrl)).hostname;
      } catch (urlError) {
        console.error('URL parsing error:', urlError);
        domain = data.websiteUrl.replace(/^https?:\/\//, '').split('/')[0];
      }

      // Check if a website with this domain already exists for this user
      const { data: existingWebsite, error: checkError } = await supabase
        .from('websites')
        .select('id, domain, is_primary')
        .eq('user_id', user.id)
        .eq('domain', domain)
        .single();

      if (checkError && checkError.code !== 'PGRST116') {
        console.error('Website check error:', checkError);
        throw checkError;
      }

      let websiteData;
      if (existingWebsite) {
        // Set all other websites for this user to is_primary: false
        const { error: updateOtherError } = await supabase
          .from('websites')
          .update({ is_primary: false })
          .eq('user_id', user.id)
          .neq('id', existingWebsite.id);

        if (updateOtherError) {
          console.error('Website update error:', updateOtherError);
          // Continue anyway
        }

        // Update existing website
        console.log('Updating existing website:', existingWebsite.id);
        const { data: updatedWebsite, error: updateError } = await supabase
          .from('websites')
          .update({
            title: data.websiteData?.title || `${domain} - Business Website`,
            description: data.websiteData?.description || `Official website for ${data.organizationName}`,
            favicon_url: data.websiteData?.favicon || `https://www.google.com/s2/favicons?domain=${domain}&sz=64`,
            is_primary: true,
            business_profile: {
              logo_url: data.websiteData?.image,
              brand_colors: [],
              tags: data.tags,
              organization_name: data.organizationName,
              website_url: normalizeUrl(data.websiteUrl)
            }
          })
          .eq('id', existingWebsite.id)
          .select()
          .single();

        if (updateError) {
          console.error('Website update error:', updateError);
          throw updateError;
        }
        websiteData = updatedWebsite;
      } else {
        // Set all existing websites for this user to is_primary: false
        const { error: updateError } = await supabase
          .from('websites')
          .update({ is_primary: false })
          .eq('user_id', user.id);

        if (updateError) {
          console.error('Website update error:', updateError);
          // Continue anyway, this might fail if no websites exist
        }

        // Create new website record with OpenGraph data
        const websiteInsertData = {
          user_id: user.id,
          domain: domain,
          title: data.websiteData?.title || `${domain} - Business Website`,
          description: data.websiteData?.description || `Official website for ${data.organizationName}`,
          favicon_url: data.websiteData?.favicon || `https://www.google.com/s2/favicons?domain=${domain}&sz=64`,
          is_primary: true,
          business_profile: {
            logo_url: data.websiteData?.image,
            brand_colors: [],
            tags: data.tags,
            organization_name: data.organizationName,
            website_url: normalizeUrl(data.websiteUrl)
          }
        };
        
        console.log('Creating website with data:', websiteInsertData);
        
        const { data: newWebsiteData, error: websiteError } = await supabase
          .from('websites')
          .insert(websiteInsertData)
          .select()
          .single();

        if (websiteError) {
          console.error('Website creation error:', websiteError);
          throw websiteError;
        }
        websiteData = newWebsiteData;
      }

      // Mark that user just completed onboarding (for dashboard to know not to show paywall immediately)
      sessionStorage.setItem(`onboarding_completed_${user.id}`, 'true');
      
      // Automatically redirect to checkout for new users
      await redirectToCheckout();
    } catch (error) {
      console.error('Error completing onboarding:', error);
      toast.error(`Error completing onboarding: ${error.message || 'Please try again.'}`);
    }
  };

  // Animate dots for redirecting message
  useEffect(() => {
    if (!redirecting) {
      setRedirectDots(0);
      return;
    }
    
    const interval = setInterval(() => {
      setRedirectDots((prev) => (prev + 1) % 4);
    }, 400);
    
    return () => clearInterval(interval);
  }, [redirecting]);

  const redirectToCheckout = async () => {
    if (!user) return;

    try {
      setRedirecting(true);
      setLoading(true);
      // Don't show toast - just redirect silently for better UX

      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        console.error('Session error:', sessionError);
        toast.error('Please log in to continue');
        setLoading(false);
        setRedirecting(false);
        return;
      }

      // Get user's country from profile if available
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('country')
        .eq('user_id', user.id)
        .single();

      // Profile error is not critical - we can proceed without country
      if (profileError && profileError.code !== 'PGRST116') {
        console.warn('Profile fetch error (non-critical):', profileError);
      }

      const POLAR_PRODUCT_ID = '6937b0e2-6510-4b4c-9470-f28f981cd131';
      const SUPABASE_URL = "https://ancujowsxivyfnxhnsmr.supabase.co";

      // Make checkout request with proper error handling
      let response: Response;
      try {
        response = await fetch(`${SUPABASE_URL}/functions/v1/polar-checkout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            product_id: POLAR_PRODUCT_ID,
            success_url: `${window.location.origin}/dashboard?subscription=success`,
            country: profile?.country || data.country || undefined,
            metadata: {
              from_onboarding: 'true',
            },
          }),
        });
      } catch (fetchError: any) {
        // Handle network errors
        setLoading(false);
        setRedirecting(false);
        if (fetchError.message?.includes('Failed to fetch') || fetchError.message?.includes('NetworkError')) {
          toast.error('Network error. Please check your connection and try again.');
        } else {
          toast.error('Unable to connect to checkout service. Please try again.');
        }
        return;
      }

      // Handle response with better error checking
      let checkoutData: any;
      
      try {
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          const text = await response.text();
          console.error('Non-JSON response:', text);
          setLoading(false);
          setRedirecting(false);
          toast.error('Invalid response from server. Please try again.');
          return;
        }
        
        checkoutData = await response.json();
      } catch (parseError) {
        console.error('Error parsing response:', parseError);
        setLoading(false);
        setRedirecting(false);
        toast.error('Invalid response from server. Please try again.');
        return;
      }

      if (!response.ok) {
        setLoading(false);
        setRedirecting(false);
        
        // Check if subscription was synced (successful sync response)
        if (checkoutData.synced) {
          setTimeout(() => {
            navigate('/dashboard', { replace: true });
          }, 500);
          return;
        }
        
        // Extract error message
        let errorMessage = 'Unable to start subscription process. Please try again.';
        if (checkoutData.error) {
          if (typeof checkoutData.error === 'string') {
            errorMessage = checkoutData.error;
          } else if (checkoutData.error.message) {
            errorMessage = checkoutData.error.message;
          }
        }
        
        // Clean up error messages
        const errorLower = errorMessage.toLowerCase();
        if (errorLower.includes('polar') || 
            errorLower.includes('checkout') ||
            errorLower.includes('customer') ||
            errorLower.includes('failed to load')) {
          errorMessage = 'Unable to start subscription process. Please try again.';
        }
        
        console.error('Checkout API error:', errorMessage, checkoutData);
        toast.error(errorMessage);
        return;
      }

      // Check if subscription was synced (successful sync response)
      if (checkoutData.synced) {
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 500);
        return;
      }

      if (!checkoutData.checkout_url) {
        console.error('No checkout_url in response:', checkoutData);
        setLoading(false);
        setRedirecting(false);
        toast.error('Invalid checkout response. Please try again.');
        return;
      }

      // Track checkout initiation (non-blocking - don't fail if this errors)
      try {
        trackInitiateCheckout({
          content_name: 'Subscription Checkout',
          content_category: 'Subscription',
          value: 87.90,
          currency: 'USD',
          num_items: 1
        });
      } catch (trackError) {
        // Don't fail checkout if tracking fails
        console.warn('Failed to track checkout initiation:', trackError);
      }
      
      // Store flag that we're going to checkout (so we know when they return)
      sessionStorage.setItem(`going_to_checkout_${user.id}`, 'true');
      
      // Success - redirect to checkout
      window.location.href = checkoutData.checkout_url;
    } catch (error: any) {
      console.error('Checkout redirect error:', error);
      setLoading(false);
      setRedirecting(false);
      
      // Provide user-friendly error messages
      let errorMessage = 'Unable to start subscription process. Please try again.';
      
      if (error) {
        if (typeof error === 'string') {
          errorMessage = error;
        } else if (error.message) {
          errorMessage = error.message;
        } else if (error.error) {
          if (typeof error.error === 'string') {
            errorMessage = error.error;
          } else if (error.error.message) {
            errorMessage = error.error.message;
          }
        }
      }
      
      // Clean up error messages (remove technical details and generic "failed to load")
      const errorLower = errorMessage.toLowerCase();
      if (errorLower.includes('polar') || 
          errorLower.includes('checkout') ||
          errorLower.includes('customer') ||
          errorLower.includes('failed to load') ||
          errorLower.includes('fetch') ||
          errorLower.includes('network')) {
        errorMessage = 'Unable to start subscription process. Please try again.';
      }
      
      toast.error(errorMessage);
      // Don't redirect on error - let user try again or go back
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-extrabold text-foreground mb-2 tracking-tight">
            {step === 1 && "Tell us your brand name"}
            {step === 2 && "Add relevant tags"}
            {step === 3 && "Let's connect your website"}
          </h1>
          <p className="text-lg text-muted-foreground mb-2">
            {step === 1 && "This helps us personalize your experience."}
            {step === 2 && "Choose tags that best describe your business."}
            {step === 3 && "We'll analyze your site to get started."}
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex flex-col items-center mb-12 w-full">
          {/* Stepper Container */}
          <div className="relative w-full max-w-xs flex flex-col items-center">
            {/* Progress Bar */}
            <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-2 rounded-full bg-muted z-0" style={{width: '100%'}}>
              <div
                className="h-2 rounded-full bg-primary transition-all duration-500 z-10"
                style={{
                  width: `${((step - 1) / 2) * 100}%`,
                }}
              />
            </div>
            {/* Step Circles */}
            <div className="relative flex justify-between items-center w-full z-10">
              {[1, 2, 3].map((s, i) => (
                <div
                  key={s}
                  className={`flex items-center justify-center rounded-full transition-all duration-300
                    ${step === s
                      ? 'bg-primary text-primary-foreground shadow-[0_2px_12px_0_rgba(0,0,0,0.10)] scale-105'
                      : step > s
                        ? 'bg-primary text-white'
                        : 'bg-muted text-muted-foreground border border-border'}
                    ${step === s ? 'w-7 h-7 text-base font-bold' : 'w-6 h-6 text-sm'}
                  `}
                  style={{ marginTop: step === s ? '-2px' : '0' }}
                >
                  {step > s ? <CheckCircle className="w-4 h-4" /> : s}
                </div>
              ))}
            </div>
          </div>
        </div>

        <Card className="border-border shadow-xl rounded-3xl bg-background/80 backdrop-blur-md">
          <CardHeader>
            <CardTitle className="text-center text-xl font-semibold">
              {step === 1 && "Brand or Organization Name"}
              {step === 2 && "Tags"}
              {step === 3 && "Website URL"}
            </CardTitle>
          </CardHeader>
          <div
            className="onboarding-card-content"
            style={{
              height: cardContentHeight ? cardContentHeight + 'px' : 'auto',
              overflow: 'hidden',
            }}
          >
            <CardContent ref={cardContentRef} className="space-y-6">
              {step === 1 && (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="organization">Organization Name</Label>
                    <Input
                      id="organization"
                      value={data.organizationName}
                      onChange={(e) => setData(prev => ({ ...prev, organizationName: e.target.value }))}
                      placeholder="Enter your brand or organization name"
                      className="mt-1"
                    />
                  </div>
                  
                  {/* Country Selection - Always visible */}
                  <div>
                    <CountrySelector
                      value={data.country}
                      onChange={(country) => setData(prev => ({ ...prev, country }))}
                      onDetected={(country) => setData(prev => ({ ...prev, country }))}
                    />
                  </div>
                </div>
              )}
              {step === 2 && (
                <div className="space-y-4">
                  <div>
                    <Label>Available Tags</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {TAGS.map(tag => (
                        <button
                          key={tag}
                          type="button"
                          className={`px-4 py-2 rounded-full border text-sm font-medium transition-all duration-200
                            ${data.tags.includes(tag)
                              ? 'bg-primary text-primary-foreground border-primary shadow'
                              : 'bg-muted text-muted-foreground border-border hover:bg-primary/10'}
                          `}
                          onClick={() => setData(prev => ({
                            ...prev,
                            tags: prev.tags.includes(tag)
                              ? prev.tags.filter(t => t !== tag)
                              : [...prev.tags, tag]
                          }))}
                        >
                          {tag}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {step === 3 && (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="website">Enter your website address</Label>
                    <Input
                      id="website"
                      value={data.websiteUrl}
                      onChange={(e) => setData(prev => ({ ...prev, websiteUrl: e.target.value }))}
                      placeholder="https://yourwebsite.com"
                      className="mt-1"
                    />
                  </div>
                  {(loading || typing) && (
                    <div className="flex items-center gap-3 p-6 min-h-[96px] rounded-xl border border-primary/30 shadow-[0_2px_16px_0_rgba(0,0,0,0.06)] bg-background/80 backdrop-blur-sm transition-all">
                      {/* Skeleton shimmer for favicon */}
                      <div className="relative overflow-hidden rounded w-8 h-8 bg-muted-foreground/20">
                        <div className="absolute inset-0 animate-[shimmer_1.2s_infinite_linear] bg-gradient-to-r from-transparent via-white/60 to-transparent" style={{backgroundSize:'200% 100%'}} />
                      </div>
                      {/* Skeleton shimmer for text */}
                      <div className="flex-1 space-y-2">
                        <div className="relative overflow-hidden h-4 rounded w-2/3 bg-muted-foreground/20">
                          <div className="absolute inset-0 animate-[shimmer_1.2s_infinite_linear] bg-gradient-to-r from-transparent via-white/60 to-transparent" style={{backgroundSize:'200% 100%'}} />
                        </div>
                        <div className="relative overflow-hidden h-3 rounded w-1/2 bg-muted-foreground/10">
                          <div className="absolute inset-0 animate-[shimmer_1.2s_infinite_linear] bg-gradient-to-r from-transparent via-white/60 to-transparent" style={{backgroundSize:'200% 100%'}} />
                        </div>
                      </div>
                    </div>
                  )}
                  {data.websiteData && !loading && !typing && (
                    <div className="flex items-center gap-3 p-4 rounded-xl border border-primary/30 shadow-[0_2px_16px_0_rgba(0,0,0,0.06)] bg-background/80 backdrop-blur-sm transition-all">
                      <img 
                        src={data.websiteData.favicon} 
                        alt="Site favicon" 
                        className="w-8 h-8 rounded"
                        onError={(e) => {
                          e.currentTarget.src = '/placeholder.svg';
                        }}
                      />
                      <div className="flex-1">
                        <h3 className="font-medium text-foreground">{data.websiteData.title}</h3>
                        <p className="text-sm text-muted-foreground">{data.websiteData.description}</p>
                      </div>
                    </div>
                  )}
                  {redirecting && (
                    <div className="flex items-center gap-3 p-4 rounded-xl border border-primary/30 bg-primary/5 backdrop-blur-sm animate-in fade-in slide-in-from-bottom-2 duration-300">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent"></div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-foreground">
                          Setting up your checkout
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          You'll be redirected in a moment
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="flex justify-between pt-4">
                {step > 1 && (
                  <Button 
                    variant="outline" 
                    onClick={handleBack}
                    disabled={(loading || redirecting) && step === 3}
                    >
                    Back
                  </Button>
                )}
                <Button 
                  onClick={handleNext}
                  disabled={(loading || redirecting) && step === 3}
                  className={step === 1 ? "w-full" : "ml-auto"}
                >
                  {redirecting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                      <span className="flex items-center">
                        Redirecting
                        <span className="inline-block w-6 ml-1 text-left">
                          {'.'.repeat(redirectDots)}
                        </span>
                      </span>
                    </>
                  ) : (loading && step === 3) ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      {step === 3 ? "Submit" : "Next"}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default OnboardingFlow;